import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import { formatDate, titleCase } from '../../lib/format'
import type { ComplianceNotice } from '../../types'
import {
  Alert, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { CheckIcon, ShieldIcon } from '../../components/icons'

interface Inspection {
  id?: string
  date?: string
  status?: string
  inspector_uid?: string
  outcome?: string
  notes?: string
  site_id?: string
}

interface ComplianceResponse {
  ok: boolean
  notices: ComplianceNotice[]
  inspections: Inspection[]
}

export default function Compliance() {
  const { user } = useAuth()
  // Both portals expose the same org-scoped view under their own prefix.
  const base = portalFor(user) === 'leader' ? '/leader' : '/miner'

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['compliance', base],
    queryFn: () => api.get<ComplianceResponse>(`${base}/compliance`),
  })

  const notices = data?.notices ?? []
  const inspections = data?.inspections ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compliance</h1>
          <p className="page-head__sub">
            Notices and inspections recorded against your cooperative.
          </p>
        </div>
      </div>

      {/* Both lists are org-scoped server-side, so with no org there is
          genuinely nothing to show - say so rather than showing empty tables. */}
      {!user?.org_id && !isLoading && (
        <Alert tone="info">
          Your account isn&rsquo;t linked to a cooperative yet, so there are no
          compliance records to show. Ask your administrator to add you to one.
        </Alert>
      )}

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      <div className="stack" style={{ marginTop: 'var(--sp-5)' }}>
        <Card>
          <CardHeader title="Compliance notices"
                      sub={notices.length ? `${notices.length} on record` : undefined} />
          <CardBody flush>
            {isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={20} /><Skeleton h={20} w="72%" />
              </div>
            ) : notices.length === 0 ? (
              <EmptyState icon={<CheckIcon size={22} />} title="No notices"
                          body="Nothing outstanding against your cooperative." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Notice</th><th>Severity</th><th>Issued</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {notices.map((n, i) => (
                      <tr key={n.id ?? i}>
                        <td>
                          <div style={{ fontWeight: 550 }}>{n.title || 'Notice'}</div>
                          {n.description && (
                            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
                              {n.description}
                            </div>
                          )}
                        </td>
                        <td>{n.severity ? <StatusBadge status={n.severity} /> : '—'}</td>
                        <td>{formatDate(n.issued_date)}</td>
                        <td><StatusBadge status={n.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Inspections"
                      sub={inspections.length ? `${inspections.length} on record` : undefined} />
          <CardBody flush>
            {isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={20} /><Skeleton h={20} w="64%" />
              </div>
            ) : inspections.length === 0 ? (
              <EmptyState icon={<ShieldIcon size={22} />} title="No inspections yet"
                          body="Ministry inspections of your sites will be listed here." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Date</th><th>Outcome</th><th>Notes</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {inspections.map((ins, i) => (
                      <tr key={ins.id ?? i}>
                        <td>{formatDate(ins.date)}</td>
                        <td>{ins.outcome ? titleCase(ins.outcome) : '—'}</td>
                        <td style={{ maxWidth: 380 }}>{ins.notes || '—'}</td>
                        <td><StatusBadge status={ins.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}
